package fr.manyverse.app.infrastructure.realtime

import fr.manyverse.app.AppLogger
import fr.manyverse.app.domain.error.UnexpectedError
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.auth.auth
import io.github.jan.supabase.auth.status.SessionStatus
import io.github.jan.supabase.realtime.RealtimeChannel
import io.github.jan.supabase.realtime.broadcastFlow
import io.github.jan.supabase.realtime.channel
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.channels.awaitClose
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.SharedFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.buffer
import kotlinx.coroutines.flow.channelFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.emptyFlow
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.filterIsInstance
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onSubscription
import kotlinx.coroutines.flow.retryWhen
import kotlinx.coroutines.flow.shareIn
import kotlinx.coroutines.flow.transform
import kotlinx.coroutines.launch
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.contentOrNull
import kotlin.time.Duration.Companion.seconds

private const val TAG = "GameRealtimeChannel"

/** Nom d'événement des messages envoyés par `public.send_game_change` (schema_bdd.sql). */
internal const val GAME_CHANGE_EVENT = "db_change"

// Évite de quitter puis rejoindre le channel à chaque changement d'écran
private val KEEP_ALIVE = 10.seconds
private const val CHANGE_BUFFER = 64
private val RETRY_DELAY = 5.seconds
private const val MAX_RETRY_STEPS = 6L

/** Changement d'une ligne de partie, tel que l'envoient les triggers `broadcast_*_change`. */
internal data class GameChange(
    val table: String,
    val type: Type,
    val record: JsonObject?,
    val oldRecord: JsonObject?
) {
    enum class Type { INSERT, UPDATE, DELETE }

    /** La ligne concernée : sa nouvelle version, ou l'ancienne pour une suppression. */
    val row: JsonObject? get() = record ?: oldRecord
}

/** Ce que reçoit un flux de partie. */
internal sealed interface GameChannelEvent {
    /**
     * Le channel est abonné : à la première écoute, après une coupure ou un changement de
     * session. Les changements survenus avant ne sont pas rejoués : l'état complet est à (re)charger.
     */
    data object Ready : GameChannelEvent

    data class Change(val change: GameChange) : GameChannelEvent
}

/** Channel partagé par tous les flux de partie ouverts. */
internal class GameChannelSession(
    val changes: SharedFlow<GameChange>,
    /** Vrai tant que le channel est abonné ; faux pendant une coupure. */
    val isSubscribed: Flow<Boolean>
)

/**
 * Channel Realtime privé `user:<id>` de l'utilisateur connecté, unique quel que soit le nombre de
 * modules de partie ouverts.
 *
 * Les triggers de base y envoient chaque changement de partie aux seuls utilisateurs qui ont le
 * droit de le lire, selon les mêmes règles que les policies SELECT (schema_bdd.sql, section
 * REALTIME). Contrairement à Postgres Changes, Realtime ne relit pas la ligne pour chaque abonné
 * et une suppression n'est envoyée qu'aux personnes concernées, avec la ligne complète.
 *
 * Le channel est ouvert à la première écoute et quitté [KEEP_ALIVE] après la dernière.
 */
class GameRealtimeChannel(supabaseClient: SupabaseClient, scope: CoroutineScope) {

    @OptIn(ExperimentalCoroutinesApi::class)
    private val sessions: SharedFlow<GameChannelSession> = supabaseClient.authenticatedUserIds()
        .flatMapLatest { userId ->
            if (userId == null) emptyFlow() else openSession(supabaseClient, userId)
        }
        // Le partage tourne dans le scope de l'app : une erreur imprévue y crasherait l'app ou
        // couperait le temps réel jusqu'au redémarrage. Le channel est rouvert après un délai croissant
        .retryWhen { cause, attempt ->
            AppLogger.error(TAG, "Channel de partie interrompu, nouvelle tentative", cause)
            delay(RETRY_DELAY * (attempt + 1).coerceAtMost(MAX_RETRY_STEPS).toInt())
            true
        }
        .shareIn(
            scope,
            SharingStarted.WhileSubscribed(KEEP_ALIVE.inWholeMilliseconds, replayExpirationMillis = 0),
            replay = 1
        )

    /** Changements de [table] précédés de [GameChannelEvent.Ready], pour un flux qui tient un état. */
    internal fun events(table: String): Flow<GameChannelEvent> = gameChannelEvents(sessions, table)

    /** Changements de [table], pour un flux qui ne fait que relayer les nouveautés. */
    internal fun changes(table: String): Flow<GameChange> =
        events(table).filterIsInstance<GameChannelEvent.Change>().map { it.change }
}

/**
 * Relaie les changements de [table] reçus par chaque session, et émet [GameChannelEvent.Ready]
 * chaque fois que le channel devient abonné, l'écoute étant déjà en place : un état chargé à ce
 * moment ne peut manquer aucun changement. Un flux démarré pendant une coupure attend donc la
 * reconnexion pour charger son état.
 */
internal fun gameChannelEvents(
    sessions: Flow<GameChannelSession>,
    table: String
): Flow<GameChannelEvent> {
    val events = channelFlow {
        sessions.collectLatest { session ->
            coroutineScope {
                val listening = CompletableDeferred<Unit>()
                launch {
                    session.changes
                        .onSubscription { listening.complete(Unit) }
                        .collect { change ->
                            if (change.table == table) send(GameChannelEvent.Change(change))
                        }
                }
                listening.await()
                session.isSubscribed
                    .distinctUntilChanged()
                    .filter { it }
                    .collect { send(GameChannelEvent.Ready) }
            }
        }
    }
    // Le consommateur charge l'état à chaque Ready : les changements reçus entre-temps attendent
    return events.buffer(Channel.UNLIMITED)
}

/** Lit un message de `public.send_game_change` ; `null` s'il est malformé. */
internal fun decodeGameChange(payload: JsonObject): GameChange? {
    val table = (payload["table"] as? JsonPrimitive)?.contentOrNull ?: return null
    val type = (payload["type"] as? JsonPrimitive)?.contentOrNull
        ?.let { name -> GameChange.Type.entries.firstOrNull { it.name == name } }
        ?: return null
    return GameChange(
        table = table,
        type = type,
        record = payload["record"] as? JsonObject,
        oldRecord = payload["old_record"] as? JsonObject
    )
}

private fun openSession(supabaseClient: SupabaseClient, userId: String): Flow<GameChannelSession> =
    channelFlow {
        val channel = supabaseClient.channel("user:$userId") { isPrivate = true }
        val changes = MutableSharedFlow<GameChange>(extraBufferCapacity = CHANGE_BUFFER)

        launch {
            channel.broadcastFlow<JsonObject>(GAME_CHANGE_EVENT).collect { payload ->
                val change = decodeGameChange(payload)
                if (change == null) {
                    // Format fixé par les triggers : un message illisible révèle un décalage avec la base
                    AppLogger.error(
                        TAG,
                        "Message Realtime de partie illisible (clés ${payload.keys})",
                        UnexpectedError("Malformed game change message")
                    )
                } else {
                    changes.emit(change)
                }
            }
        }

        subscribeSupervised(supabaseClient, channel, TAG)
        AppLogger.debug(TAG, "Channel de partie abonné")
        launch {
            resubscriptions(channel.status).collect {
                AppLogger.info(TAG, "Channel de partie réabonné, rechargement des états")
            }
        }
        send(
            GameChannelSession(
                changes = changes.asSharedFlow(),
                isSubscribed = channel.status.map { it == RealtimeChannel.Status.SUBSCRIBED }
            )
        )
        awaitClose()
    }

/**
 * Identifiant de l'utilisateur connecté, `null` une fois déconnecté. Pendant le chargement de la
 * session ou un échec de rafraîchissement, l'utilisateur connu reste valable.
 */
private fun SupabaseClient.authenticatedUserIds(): Flow<String?> = auth.sessionStatus
    .transform { status ->
        when (status) {
            is SessionStatus.Authenticated -> emit(status.session.user?.id)
            is SessionStatus.NotAuthenticated -> emit(null)
            is SessionStatus.Initializing, is SessionStatus.RefreshFailure -> Unit
        }
    }
    .distinctUntilChanged()
